import { useRef, useEffect, useState, useCallback } from 'react'
import { Star, GripVertical, Pencil, Loader2, CheckCircle2, AlertCircle, Lightbulb } from 'lucide-react'
import * as Y from 'yjs'
import type { Idea, UserPresence } from '../types'
import IdeaModal from './IdeaModal'
import { useAppStore } from '../stores/useAppStore'
import { categoryLabel, ideaStatusLabel } from '../lib/labels'
import { generateId } from '../lib/id'

export interface CanvasAgentMark {
  id: string
  name: string
  color: string
  x: number
  y: number
  text: string
}

interface InfiniteCanvasProps {
  ideas: Y.Array<Idea>
  canvasElements: Y.Map<unknown>
  users: UserPresence[]
  activeTool: string
  currentUserId?: string
  agentStatus?: string
  onCursorMove?: (x: number, y: number) => void
  agentMarks?: CanvasAgentMark[]
}

export default function InfiniteCanvas({ ideas, canvasElements: _canvasElements, users, activeTool, currentUserId, agentStatus, onCursorMove, agentMarks = [] }: InfiniteCanvasProps) {
  const currentUserName = useAppStore((s) => s.user?.name)
  const canvasRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const panStart = useRef({ x: 0, y: 0 })
  const [ideaList, setIdeaList] = useState<Idea[]>([])
  const [draggingIdeaId, setDraggingIdeaId] = useState<string | null>(null)
  const dragOffset = useRef({ x: 0, y: 0 })
  const [editingIdea, setEditingIdea] = useState<Idea | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    const handler = () => {
      setIdeaList(ideas.toArray())
    }
    ideas.observe(handler)
    handler()
    return () => ideas.unobserve(handler)
  }, [ideas])

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    setScale((s) => Math.max(0.2, Math.min(5, s * delta)))
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 1 || (e.button === 0 && e.shiftKey)) {
      setIsPanning(true)
      panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (onCursorMove && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect()
      const x = (e.clientX - rect.left - pan.x) / scale
      const y = (e.clientY - rect.top - pan.y) / scale
      onCursorMove(x, y)
    }

    if (isPanning) {
      setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y })
    }

    if (draggingIdeaId && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect()
      const x = (e.clientX - rect.left - pan.x) / scale - dragOffset.current.x
      const y = (e.clientY - rect.top - pan.y) / scale - dragOffset.current.y

      const idx = ideas.toArray().findIndex((i) => i.id === draggingIdeaId)
      if (idx !== -1) {
        const idea = ideas.get(idx)
        if (idea) {
          ideas.delete(idx, 1)
          ideas.insert(idx, [{ ...idea, x, y }])
        }
      }
    }
  }

  const handleMouseUp = () => {
    setIsPanning(false)
    setDraggingIdeaId(null)
  }

  const handleIdeaMouseDown = (e: React.MouseEvent, idea: Idea) => {
    if (activeTool !== 'select') return
    e.stopPropagation()
    if (!canvasRef.current) return
    const rect = canvasRef.current.getBoundingClientRect()
    const mouseX = (e.clientX - rect.left - pan.x) / scale
    const mouseY = (e.clientY - rect.top - pan.y) / scale
    dragOffset.current = { x: mouseX - idea.x, y: mouseY - idea.y }
    setDraggingIdeaId(idea.id)
  }

  const openEditModal = (idea: Idea) => {
    setEditingIdea(idea)
    setIsModalOpen(true)
  }

  const openCreateModal = (x: number, y: number) => {
    setEditingIdea({
      id: generateId(),
      title: '',
      description: '',
      category: 'general',
      status: 'open',
      x,
      y,
      created_by: currentUserName || 'Anônimo',
      created_at: new Date().toISOString(),
    })
    setIsModalOpen(true)
  }

  const handleDoubleClick = (e: React.MouseEvent) => {
    // Double-click no vazio cria nova ideia
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return
    const x = (e.clientX - rect.left - pan.x) / scale
    const y = (e.clientY - rect.top - pan.y) / scale
    openCreateModal(x, y)
  }

  const handleIdeaDoubleClick = (e: React.MouseEvent, idea: Idea) => {
    e.stopPropagation()
    openEditModal(idea)
  }

  const handleSave = (idea: Idea) => {
    const arr = ideas.toArray()
    const idx = arr.findIndex((i) => i.id === idea.id)
    if (idx !== -1) {
      ideas.delete(idx, 1)
      ideas.insert(idx, [idea])
    } else {
      ideas.push([idea])
    }
  }

  const handleDelete = (id: string) => {
    const arr = ideas.toArray()
    const idx = arr.findIndex((i) => i.id === id)
    if (idx !== -1) {
      ideas.delete(idx, 1)
    }
  }

  const acceptSuggestion = (id: string) => {
    const arr = ideas.toArray()
    const idx = arr.findIndex((i) => i.id === id)
    if (idx === -1) return
    ideas.delete(idx, 1)
    ideas.insert(idx, [{ ...arr[idx], status: 'ready' }])
  }

  const discardSuggestion = (id: string) => {
    const arr = ideas.toArray()
    const idx = arr.findIndex((i) => i.id === id)
    if (idx !== -1 && arr[idx].status === 'suggested') ideas.delete(idx, 1)
  }

  const toScreen = useCallback(
    (x: number, y: number) => ({
      left: pan.x + x * scale,
      top: pan.y + y * scale,
    }),
    [pan, scale]
  )

  const statusDot = (status: string) => {
    if (status === 'ready') return 'bg-green-500'
    if (status === 'archived') return 'bg-gray-400'
    return 'bg-yellow-500'
  }

  return (
    <div
      ref={canvasRef}
      className="w-full h-full overflow-hidden cursor-grab active:cursor-grabbing bg-white dark:bg-gray-950 relative select-none"
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onDoubleClick={handleDoubleClick}
    >
      <div
        className="absolute inset-0 origin-top-left"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
        }}
      >
        {/* Grid */}
        <svg className="absolute inset-0 w-[5000px] h-[5000px] pointer-events-none opacity-20">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-gray-400 dark:text-gray-600" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {/* Ideas as cards */}
        {ideaList.map((idea) => (
          <div
            key={idea.id}
            className={`absolute px-4 py-3 bg-white dark:bg-gray-900 border rounded-lg shadow-sm hover:shadow-md transition-shadow group ${
              idea.status === 'suggested'
                ? 'border-dashed border-agent/60 bg-agent/5'
                : 'border-gray-200 dark:border-gray-700'
            }`}
            style={{
              left: idea.x,
              top: idea.y,
              width: 220,
              cursor: activeTool === 'select' ? 'grab' : 'default',
            }}
            onMouseDown={(e) => handleIdeaMouseDown(e, idea)}
            onDoubleClick={(e) => handleIdeaDoubleClick(e, idea)}
          >
            {idea.status === 'suggested' && (
              <div className="flex items-center gap-1 mb-1 text-[10px] font-medium text-accent">
                <Star size={9} className="shrink-0" aria-hidden="true" />
                Sugerida por {idea.created_by}
              </div>
            )}
            <div className="flex items-center gap-1 mb-1">
              <GripVertical size={12} className="text-gray-400 shrink-0" />
              <div className="text-xs font-semibold text-agent truncate flex-1">{idea.title}</div>
              {activeTool === 'select' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    openEditModal(idea)
                  }}
                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-opacity"
                  title={`Editar ideia "${idea.title}"`}
                  aria-label={`Editar ideia "${idea.title}"`}
                >
                  <Pencil size={10} aria-hidden="true" />
                </button>
              )}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 line-clamp-3">
              {idea.description}
            </div>
            {idea.status === 'suggested' && (
              <div className="mt-1 flex gap-1.5">
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); acceptSuggestion(idea.id) }}
                  className="h-6 px-2 rounded-md bg-accent text-white text-[11px] font-semibold cursor-pointer"
                >
                  Aceitar
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); discardSuggestion(idea.id) }}
                  className="h-6 px-2 rounded-md border border-line text-muted text-[11px] cursor-pointer"
                >
                  Descartar
                </button>
              </div>
            )}
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                {categoryLabel(idea.category)}
              </span>
              <span className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
                <span className={`w-1.5 h-1.5 rounded-full ${statusDot(idea.status)}`} />
                {ideaStatusLabel(idea.status)}
              </span>
              {idea.status === 'ready' && agentStatus && (
                <span className="ml-auto" title={
                  agentStatus.includes('Analisando') || agentStatus.includes('detectadas')
                    ? 'Agente analisando...'
                    : agentStatus.includes('gerada') || agentStatus.includes('✅')
                    ? 'Analisada pelo agente'
                    : agentStatus.includes('Erro') || agentStatus.includes('❌')
                    ? 'Erro na análise'
                    : ''
                }>
                  {agentStatus.includes('Analisando') || agentStatus.includes('detectadas') ? (
                    <Loader2 size={12} className="text-agent animate-spin" />
                  ) : agentStatus.includes('gerada') || agentStatus.includes('✅') ? (
                    <CheckCircle2 size={12} className="text-green-500" />
                  ) : agentStatus.includes('Erro') || agentStatus.includes('❌') ? (
                    <AlertCircle size={12} className="text-red-500" />
                  ) : null}
                </span>
              )}
            </div>
          </div>
        ))}
        {agentMarks.map((mark) => (
          <div key={mark.id} className="absolute z-10 pointer-events-none" style={{ left: mark.x, top: mark.y }}>
            <svg width="18" height="18" viewBox="0 0 24 24" className="drop-shadow-sm">
              <path d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 01.35-.15h6.87a.5.5 0 00.35-.85L6.35 2.85a.5.5 0 00-.85.35z" fill={mark.color} stroke="white" strokeWidth="1.2" />
            </svg>
            <div className="ml-4 -mt-1 max-w-[200px] rounded-lg px-2 py-1.5 text-[11px] leading-snug text-white shadow-sm" style={{ background: mark.color }}>
              <div className="font-semibold">{mark.name}</div>
              {mark.text}
            </div>
          </div>
        ))}
      </div>

      {/* User cursors overlay */}
      {users.filter((u) => u.id !== currentUserId).map((user) => {
        if (!user.cursor) return null
        const isAgent = user.id.startsWith('agent') || user.name.toLowerCase().includes('agent')
        const pos = toScreen(user.cursor.x, user.cursor.y)
        return (
          <div
            key={user.id}
            className="absolute pointer-events-none z-20"
            style={{ left: pos.left, top: pos.top }}
          >
            <div className="relative">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
                <path
                  d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 01.35-.15h6.87a.5.5 0 00.35-.85L6.35 2.85a.5.5 0 00-.85.35z"
                  fill={isAgent ? '#7C3AED' : (user.color || '#3B82F6')}
                  stroke="white"
                  strokeWidth="1"
                />
              </svg>
              <div
                className={`absolute left-4 top-4 px-1.5 py-0.5 rounded text-[10px] font-medium text-white whitespace-nowrap ${
                  isAgent ? 'bg-agent agent-cursor' : ''
                }`}
                style={{ backgroundColor: isAgent ? undefined : (user.color || '#3B82F6') }}
              >
                {user.name}
                {isAgent && <Star size={8} className="inline ml-0.5" />}
              </div>
            </div>
          </div>
        )
      })}

      {/* Onboarding: canvas vazio ensina o gesto de criação e navegação */}
      {ideaList.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="max-w-sm text-center px-6 py-5 rounded-xl bg-white/85 dark:bg-gray-900/85 backdrop-blur border border-gray-200 dark:border-gray-700 shadow-sm">
            <Lightbulb size={22} className="mx-auto text-agent mb-2" aria-hidden="true" />
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
              Dê um duplo clique em qualquer lugar para criar a primeira ideia
            </p>
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              Marque-a como <strong>pronta</strong> e o time de agentes gera SDD, diagramas,
              Event Storm e protótipo automaticamente.
            </p>
            <p className="mt-2 text-[10px] text-gray-400 dark:text-gray-500">
              Mover o canvas: Shift + arrastar (ou botão do meio) · Zoom: roda do mouse
            </p>
          </div>
        </div>
      )}

      {/* Zoom controls */}
      <div className="absolute bottom-4 left-4 flex gap-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm p-1 z-10">
        <button
          onClick={() => setScale((s) => Math.max(0.2, s - 0.2))}
          aria-label="Diminuir zoom"
          title="Diminuir zoom"
          className="px-2 py-1 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
        >
          −
        </button>
        <span className="px-2 py-1 text-xs text-gray-500 min-w-[3rem] text-center" aria-live="polite">
          {Math.round(scale * 100)}%
        </span>
        <button
          onClick={() => setScale((s) => Math.min(5, s + 0.2))}
          aria-label="Aumentar zoom"
          title="Aumentar zoom"
          className="px-2 py-1 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
        >
          +
        </button>
        <button
          onClick={() => {
            setScale(1)
            setPan({ x: 0, y: 0 })
          }}
          aria-label="Redefinir visualização"
          title="Redefinir visualização (100%)"
          className="px-2 py-1 text-[10px] font-medium text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
        >
          Reset
        </button>
      </div>

      {/* Active tool indicator */}
      <div className="absolute top-4 left-4 z-10">
        <div className="px-2 py-1 rounded-md bg-white/80 dark:bg-gray-900/80 backdrop-blur text-xs text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
          Ferramenta: {activeTool === 'select' ? 'Selecionar' : activeTool === 'idea' ? 'Ideia' : activeTool === 'agent' ? 'Agente' : activeTool}
        </div>
      </div>

      <IdeaModal
        idea={editingIdea}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </div>
  )
}
